import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Volume2, 
 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle, 
  XCircle,
 
  RotateCw,
  ChevronDown,
  ChevronUp,
  Check,
  BookOpen,
  PartyPopper,
  Sparkles,
  Target,
  X,
} from 'lucide-react';
import FloatingBackButton from '../FloatingBackButton';
import { useRevisionProgress,  GrammarWordInfo } from '../../hooks/useRevisionProgress';
import { shuffleArray } from '../../utils/utils';
import { getLanguageData } from '../../data/languages';
import RevisionStatsModal from './RevisionStatsModal';
import {
  WordData,
  LanguageCode,
} from '../../types/types';
import ProgressPie from '../ProgressPie';
import './RevisionMode.css';
import TribalBorder from '../TribalBorder';
import { useUserProgress } from '../../hooks/hooks';
import { useSupabaseAudio } from '../../hooks/useSupabaseAudio'; // ✅ Nouveau système
import { useTheme } from '../ThemeContext';
import { cleanParentheses } from '../../utils/cleanParentheses';
import { getWordTranslation } from '../../utils/localizedContent';
import { displayCategoryLabel } from '../../utils/categoryLabels';
// Note: ExitConfirmationDialog importé mais utilisation remplacée par ConfirmationModal
import ConfirmationModal from '../ConfirmationModal';
import SummaryScreen from '../SummaryScreen';
import { getRandomCongrats, getRandomProverb, getSentierName } from '../../data/summaryData';
import { EmojiText } from '../common/Picto';

interface RevisionModeProps {
  languageCode: LanguageCode;
  onBackToMenu: () => void;
  onStartLearning?: () => void;
  onWordRevised?: (word: string, isCorrect: boolean) => void;
  onSessionComplete?: () => void;
  onUpdateRevisionState?: (state: any) => void;
  checkDailyLimit?: (limitType: 'dailyRevisions' | 'exercisesPerDay') => boolean;
  /** Remonte le nombre exact de mots dus (calculé par getDueWordsForCategory) vers le parent */
  onDueWordsCountChange?: (count: number) => void;
  onShare?: () => void;
}

interface GrammarProgress {
  subcategory: string;
  masteredWords: {
    word: string;
    data: {
      translation: string;
      explanation?: string;
      example?: string;
    }
  }[];
  date: number;
}



interface WordPair {
  id: string;
  word: string;
  translation: string;
  audio?: string;
  matched: boolean;
}



interface DraggedItem {
  id: string;
  type: 'word' | 'translation';
  content: string;
  audio?: string;
}

// Nouveau type pour les exercices mixtes avec variantes
interface RevisionExercise {
  id: string;
  type: 'qcm' | 'matching';
  word: string;
  wordData: WordData;
  options?: string[]; // Pour QCM
  pairs?: WordPair[]; // Pour appariement
  variant?: 'classic' | 'reverse'; // Pour QCM : classique ou inversé
  targetWord?: string; // Pour appariement : le mot principal à tester
}

type SessionPhase = 'category-selection' | 'exercise' | 'complete';

const RevisionMode: React.FC<RevisionModeProps> = ({
  languageCode,
  onBackToMenu,
  onStartLearning,
  onWordRevised,
  onSessionComplete,
  onDueWordsCountChange,
  onShare,
}) => {
  const { playWord } = useSupabaseAudio(languageCode);
  const { t, i18n } = useTranslation();
  const {
  revisionProgress,
  updateRevisionProgress,
  finishSession,
  resetSession,
  loadSessionHistory,
  refreshRevisionProgress,
  isLoadingProgress,
} = useRevisionProgress(languageCode);

  const languageData = getLanguageData(languageCode);
  const carouselRef = useRef<HTMLDivElement>(null);

  // États de base
  const [currentWords, setCurrentWords] = useState<[string, WordData][]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [sessionPhase, setSessionPhase] = useState<SessionPhase>('category-selection');

  // Stabilise le proverbe et l'encouragement — recalculés uniquement au changement de phase
  const summaryCongrats = useMemo(() => getRandomCongrats(languageCode), [sessionPhase]);
  const summaryProverb  = useMemo(() => getRandomProverb(languageCode),  [sessionPhase]);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);

  // États pour l'affichage de la liste des mots
  const [showWordsList, setShowWordsList] = useState(false);

  // États pour la session mixte
  const [currentExercises, setCurrentExercises] = useState<RevisionExercise[]>([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentExercise, setCurrentExercise] = useState<RevisionExercise | null>(null);

  // États de navigation
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // États spécifiques à la grammaire
  const [isGrammarMode, setIsGrammarMode] = useState(false);
  const [grammarSubCategory, setGrammarSubCategory] = useState<string | null>(null);
  const [grammarType, setGrammarType] = useState<'rule' | 'conjugation' | 'vocabulary'>('rule');
  const [wordDataMap, setWordDataMap] = useState<Record<string, Record<string, WordData>>>({});

  // Map complète de toutes les catégories pour le modal d'historique
  // (wordDataMap ne couvre que la catégorie sélectionnée en session)
  const fullWordDataMap = React.useMemo<Record<string, Record<string, WordData>>>(() => {
    const map: Record<string, Record<string, WordData>> = {};
    Object.entries(languageData.categories).forEach(([catName, catWords]) => {
      if (typeof catWords === 'object' && catWords !== null) {
        map[catName] = {};
        Object.entries(catWords as Record<string, WordData>)
          .filter(([key]) => !key.includes('_'))
          .forEach(([word, data]) => {
            map[catName][cleanParentheses(word)] = data as WordData;
          });
      }
    });
    return map;
  }, [languageData.categories]);

  // Kama is read by its hieroglyphic writing, not its Latin transliteration
  // — showing "sn it" / "ḫrd" as QCM option text defeats the point of a
  // hieroglyph-reading app. Flat cleanedWord → illustration lookup (built
  // once from fullWordDataMap above) lets the render below swap in the
  // real glyphs for display only; the underlying option string (used for
  // click handling and correctness checks) is untouched.
  const kamaIllustrationByWord = React.useMemo<Record<string, string>>(() => {
    if (languageCode !== 'ka') return {};
    const map: Record<string, string> = {};
    Object.values(fullWordDataMap).forEach(catWords => {
      Object.entries(catWords).forEach(([word, data]) => {
        if (data.illustration && !map[word]) {
          map[word] = data.illustration;
        }
      });
    });
    return map;
  }, [languageCode, fullWordDataMap]);

  const kamaDisplay = useCallback((word: string): string => {
    if (languageCode !== 'ka') return word;
    return kamaIllustrationByWord[cleanParentheses(word)] ?? word;
  }, [languageCode, kamaIllustrationByWord]);

  // États pour le test d'appariement
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [selectedTranslation, setSelectedTranslation] = useState<string | null>(null);
  const [matches, setMatches] = useState<Map<string, string>>(new Map());
  const [incorrectMatches, setIncorrectMatches] = useState<Set<string>>(new Set());
  const [matchAttempts, setMatchAttempts] = useState<Map<string, number>>(new Map());
  const [draggedItem, setDraggedItem] = useState<DraggedItem | null>(null);
  const [dropZoneActive, setDropZoneActive] = useState<string | null>(null);
  
  // État pour l'ordre fixe des traductions dans l'appariement
  const [shuffledTranslations, setShuffledTranslations] = useState<WordPair[]>([]);

  // Hook de progression utilisateur
  const { userProgress } = useUserProgress(languageCode, selectedCategory || '');
  const { theme } = useTheme();

  // État des statistiques et données de session
  const [sessionStats, setSessionStats] = useState({
    correctAnswers: 0,
    totalAttempts: 0,
    grammarProgress: {
      rules: 0,
      conjugations: 0,
      vocabulary: 0
    } as Record<string, number> 
  });

  const [tempSessionData, setTempSessionData] = useState<Array<{
    word: string;
    isCorrect: boolean;
    category: string;
    timestamp: number;
    exerciseType: 'qcm' | 'qcm-reverse' | 'matching';
    attempts: number;
  }>>([]);

  // États pour la gestion de sauvegarde
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);

  // Fonction pour vérifier si un fichier audio est valide
  const hasValidAudio = (audio: string | undefined): boolean => {
    return !!audio && audio.trim() !== '';
  };

  // Fonction pour forcer la migration au démarrage
  const forceMigrationOnStart = useCallback(() => {
  
    
    try {
      // Vérifier s'il y a des mots dans l'ancien système global
      const globalLearnedKey = `${languageCode}-allLearnedWords`;
      const savedGlobalLearned = localStorage.getItem(globalLearnedKey);
      
      if (savedGlobalLearned) {
        const globalWords = JSON.parse(savedGlobalLearned) as string[];
       
        // Forcer la répartition même si la migration a déjà été faite
        Object.keys(languageData.categories).forEach(categoryName => {
          const categoryWords = languageData.categories[categoryName];
          if (!categoryWords) return;
          
          const categoryLearnedKey = `${languageCode}-${categoryName}-learnedWords`;
          let categoryLearnedWords: string[] = [];
          
          // Charger la liste existante
          try {
            const saved = localStorage.getItem(categoryLearnedKey);
            if (saved) {
              categoryLearnedWords = JSON.parse(saved) as string[];
            }
          } catch (error) {
}
          
          let hasNewWords = false;
          
          Object.keys(categoryWords).forEach(word => {
            if (word.includes('_')) return;
            
            const cleanWord = cleanParentheses(word);
            
            // Si le mot est dans la liste globale mais PAS dans la catégorie
            if ((globalWords.includes(cleanWord) || globalWords.includes(word)) && 
                !categoryLearnedWords.includes(cleanWord)) {
              categoryLearnedWords.push(cleanWord);
              hasNewWords = true;
          
            }
          });
          
          // Sauvegarder si il y a de nouveaux mots
          if (hasNewWords) {
            localStorage.setItem(categoryLearnedKey, JSON.stringify(categoryLearnedWords));
         
          }
        });
      }
      
      // Marquer la migration comme terminée
      const migrationKey = `${languageCode}-forced-migration-completed`;
      localStorage.setItem(migrationKey, 'true');
      
    } catch (error) {
}
  }, [languageCode, languageData.categories]);


  

  // Fonctions de navigation et défilement
  const scroll = useCallback((direction: 'left' | 'right') => {
    if (!carouselRef.current) return;
    const container = carouselRef.current;
    const scrollAmount = 320;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  }, []);

  const handleScroll = useCallback(() => {
    if (!carouselRef.current) return;
    const container = carouselRef.current;
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(
      container.scrollLeft < container.scrollWidth - container.clientWidth
    );
  }, []);

const isWordDueForRevision = useCallback((word: string, category: string, grammarType?: string): boolean => {
  const cleanWord = cleanParentheses(word);
  // Use localStorage history (more complete than currentSessionWords which is cleared on reset)
  const history = loadSessionHistory();
  const now = Date.now();

  // Filtrer l'historique pour ce mot spécifique dans cette catégorie
  const wordHistory = history.filter(item => {
    const itemCleanWord = cleanParentheses(item.word);

    if (grammarType && 'grammarType' in item) {
      // Correspondance exacte grammarType
      const grammarItem = item as GrammarWordInfo;
      return itemCleanWord === cleanWord &&
             grammarItem.grammarType === grammarType &&
             item.category === category;
    } else if (!grammarType && 'grammarType' in item) {
      // Pas de grammarType précisé mais l'enregistrement est de grammaire :
      // inclure quand même pour que lastReview.nextReview soit lisible
      return itemCleanWord === cleanWord && item.category === category;
    } else if (!grammarType && !('grammarType' in item)) {
      return itemCleanWord === cleanWord && item.category === category;
    }

    return false;
  }).sort((a, b) => b.timestamp - a.timestamp);

  // Vérification : Mot appris dans cette catégorie ?
  let isWordLearned = false;
  
  if (category === 'Grammaire') {
  // ✅ PRIORITÉ 1: Vérifier dans userProgress.recentlyLearnedWords
  if (userProgress?.recentlyLearnedWords && Array.isArray(userProgress.recentlyLearnedWords)) {
    isWordLearned = userProgress.recentlyLearnedWords.some(item => {
      if (item.category !== 'Grammaire') return false;
      const itemCleanWord = cleanParentheses(item.word);
      return itemCleanWord === cleanWord || item.word === word;
    });
    
    if (isWordLearned) {
     
    }
  }
  
  // 2. FALLBACK: localStorage (pour compatibilité)
  if (!isWordLearned) {
    const storageKey = `grammar-progress-${languageCode}`;
    try {
      const savedProgress = localStorage.getItem(storageKey);
      if (savedProgress) {
        const progress = JSON.parse(savedProgress) as GrammarProgress[];
        const allGrammarWords = progress.flatMap((p: GrammarProgress) => p.masteredWords);
        
        isWordLearned = allGrammarWords.some(wordData => {
          const grammarCleanWord = cleanParentheses(wordData.word);
          return grammarCleanWord === cleanWord || wordData.word === word;
        });
      }
    } catch (error) {
}
  }
}
   
   else {
    // ✅ PRIORITÉ 1: Vérifier dans userProgress.recentlyLearnedWords
    if (userProgress?.recentlyLearnedWords && Array.isArray(userProgress.recentlyLearnedWords)) {
      isWordLearned = userProgress.recentlyLearnedWords.some(item => {
        if (item.category !== category) return false;
        const itemCleanWord = cleanParentheses(item.word);
        return itemCleanWord === cleanWord || item.word === word;
      });
      
      if (isWordLearned) {
      
      }
    }
    
    // 2. FALLBACK: localStorage (pour compatibilité)
    if (!isWordLearned) {
      const categoryLearnedKey = `${languageCode}-${category}-learnedWords`;
      try {
        const savedCategoryLearned = localStorage.getItem(categoryLearnedKey);
        if (savedCategoryLearned) {
          const categoryWords = JSON.parse(savedCategoryLearned) as string[];
          isWordLearned = categoryWords.includes(cleanWord) || categoryWords.includes(word);
        }
      } catch (error) {
}
    }
  }

  // Dernier recours: si le mot a une clé dans wordsToReview, il a forcément été appris
  if (!isWordLearned) {
    const wordsToReviewArr = Array.from(revisionProgress.wordsToReview || []);
    isWordLearned = wordsToReviewArr.some(key => {
      if (key.startsWith('grammar:')) return false;
      const parts = key.split(':');
      if (parts.length < 3) return false;
      return parts[1] === category && cleanParentheses(parts.slice(2).join(':')) === cleanParentheses(word);
    });
    if (isWordLearned) {
    }
  }

  // Si le mot n'a jamais été appris DANS CETTE CATÉGORIE, il ne peut pas être révisé
  if (!isWordLearned) {
return false;
  }

  // Si pas d'historique de révision, le mot est dû pour sa première révision
  if (wordHistory.length === 0) {
    // ✅ Vérifier dans wordsToReview avec support du préfixe "grammar:"
    const wordsToReview = Array.from(revisionProgress.wordsToReview || []);

    const isInWordsToReview = wordsToReview.some(key => {
      let keyCategory = '';
      let keyWord = '';

      if (key.startsWith('grammar:')) {
        // Format grammaire: "grammar:Grammaire:subcategory:word"
        const parts = key.split(':');
        if (parts.length >= 4) {
          keyCategory = parts[1]; // "Grammaire"
          keyWord = parts.slice(3).join(':'); // "Maa ngi"
        }
      } else {
        // Format vocabulaire: "wf:Temps:Àjjuma"
        const parts = key.split(':');
        if (parts.length >= 3) {
          keyCategory = parts[1];
          keyWord = parts.slice(2).join(':');
        }
      }

      const keyCleanWord = cleanParentheses(keyWord);
      const currentCleanWord = cleanParentheses(word);

      return keyCategory === category &&
             (keyCleanWord === currentCleanWord || keyWord === word);
    });

    if (isInWordsToReview) {
      return true;
    }

    // Le mot est appris (isWordLearned = true) mais absent de wordsToReview ET sans historique.
    // Cela arrive quand le localStorage a été effacé (clearProgressLocalStorage) et que la
    // restauration Supabase a échoué ou n'est pas encore terminée.
    // Dans ce cas, on affiche le mot pour révision plutôt que de le masquer définitivement.
    return true;
  }

  const lastReview = wordHistory[0];

  // Vérifier explicitement si le mot a une nextReview programmée
  if (lastReview.nextReview && typeof lastReview.nextReview === 'number') {
    const isDue = lastReview.nextReview <= now;
    const nextReviewIn = Math.round((lastReview.nextReview - now) / 60000);
return isDue;
  }
  
  // FALLBACK : Si pas de nextReview définie, calculer selon l'historique
  const daysSinceLastReview = (now - lastReview.timestamp) / (24 * 60 * 60 * 1000);
  
  // Compter les succès consécutifs depuis le début de l'historique
  let consecutiveCorrect = 0;
  for (const review of wordHistory) {
    if (review.isCorrect) {
      consecutiveCorrect++;
    } else {
      break;
    }
  }
  
  // Intervalles basés sur les succès consécutifs
  let revisionInterval: number;
  switch (consecutiveCorrect) {
    case 0:
      revisionInterval = 0; // Échec récent : révision immédiate
      break;
    case 1:
      revisionInterval = 1; // 1er succès : révision dans 1 jour
      break;
    case 2:
      revisionInterval = 3; // 2ème succès : révision dans 3 jours
      break;
    case 3:
      revisionInterval = 7; // 3ème succès : révision dans 1 semaine
      break;
    case 4:
      revisionInterval = 14; // 4ème succès : révision dans 2 semaines
      break;
    default:
      revisionInterval = 30; // 5+ succès : révision dans 1 mois
      break;
  }
  
  const isDue = daysSinceLastReview >= revisionInterval;

  return isDue;
}, [loadSessionHistory, languageCode, userProgress, revisionProgress]);

  // Fonction pour vérifier et corriger les doublons inter-catégories
  const checkAndCleanCrossCategory = useCallback(() => {
 
    
    // Pour chaque catégorie, vérifier que les mots lui appartiennent vraiment
    Object.keys(languageData.categories).forEach(categoryName => {
      if (categoryName === 'Grammaire') return; // Skip grammaire
      
      const categoryLearnedKey = `${languageCode}-${categoryName}-learnedWords`;
      const categoryWords = languageData.categories[categoryName];
      
      if (!categoryWords) return;
      
      try {
        const savedCategoryLearned = localStorage.getItem(categoryLearnedKey);
        if (!savedCategoryLearned) return;
        
        const learnedWords = JSON.parse(savedCategoryLearned) as string[];
        const validWords: string[] = [];
        const invalidWords: string[] = [];
        
        // Vérifier chaque mot appris
        learnedWords.forEach(learnedWord => {
          const cleanLearnedWord = cleanParentheses(learnedWord);
          
          // Vérifier si le mot existe dans cette catégorie
          const existsInCategory = Object.keys(categoryWords).some(categoryWord => {
            if (categoryWord.includes('_')) return false;
            const cleanCategoryWord = cleanParentheses(categoryWord);
            return cleanCategoryWord === cleanLearnedWord || categoryWord === learnedWord;
          });
          
          if (existsInCategory) {
            validWords.push(learnedWord);
          } else {
            invalidWords.push(learnedWord);
         
          }
        });
        
        // Sauvegarder seulement les mots valides
        if (invalidWords.length > 0) {
          localStorage.setItem(categoryLearnedKey, JSON.stringify(validWords));
     
        }
        
      } catch (error) {
      
      }
    });
  }, [languageCode, languageData.categories]);

  // Fonction pour synchroniser depuis LearnMode
  const syncFromLearnMode = useCallback(() => {
    
    
    // Vérifier s'il y a de nouveaux mots dans l'ancien système global
    try {
      const globalLearnedKey = `${languageCode}-allLearnedWords`;
      const savedGlobalLearned = localStorage.getItem(globalLearnedKey);
      
      if (savedGlobalLearned) {
        const globalWords = JSON.parse(savedGlobalLearned) as string[];
        
        // Pour chaque catégorie, vérifier s'il y a de nouveaux mots à migrer
        Object.keys(languageData.categories).forEach(categoryName => {
          if (categoryName === 'Grammaire') return;
          
          const categoryWords = languageData.categories[categoryName];
          if (!categoryWords) return;
          
          const categoryLearnedKey = `${languageCode}-${categoryName}-learnedWords`;
          let categoryLearnedWords: string[] = [];
          
          try {
            const saved = localStorage.getItem(categoryLearnedKey);
            if (saved) {
              categoryLearnedWords = JSON.parse(saved) as string[];
            }
          } catch (error) {
return;
          }
          
          let hasNewWords = false;
          
          // Chercher les mots globaux qui appartiennent à cette catégorie
          globalWords.forEach(globalWord => {
            const cleanGlobalWord = cleanParentheses(globalWord);
            
            // Vérifier si le mot appartient à cette catégorie ET n'est pas déjà dans la liste
            const belongsToCategory = Object.keys(categoryWords).some(categoryWord => {
              if (categoryWord.includes('_')) return false;
              const cleanCategoryWord = cleanParentheses(categoryWord);
              return cleanCategoryWord === cleanGlobalWord || categoryWord === globalWord;
            });
            
            if (belongsToCategory && !categoryLearnedWords.includes(cleanGlobalWord)) {
              categoryLearnedWords.push(cleanGlobalWord);
              hasNewWords = true;
           
            }
          });
          
          // Sauvegarder si il y a de nouveaux mots
          if (hasNewWords) {
            localStorage.setItem(categoryLearnedKey, JSON.stringify(categoryLearnedWords));
           
          }
        });
      }
    } catch (error) {
}
  }, [languageCode, languageData.categories]);

const getDueWordsForCategory = useCallback((category: string): [string, WordData][] => {
  // Diagnostic: mots dans wordsToReview pour cette catégorie mais absents de recentlyLearnedWords
  const learnedSet = new Set((userProgress?.recentlyLearnedWords || []).filter(i => i.category === category).map(i => cleanParentheses(i.word)));
  const orphanKeys = Array.from(revisionProgress.wordsToReview).filter(key => {
    if (key.startsWith('grammar:')) return false;
    const parts = key.split(':');
    return parts.length >= 3 && parts[1] === category && !learnedSet.has(cleanParentheses(parts.slice(2).join(':')));
  });
  if (orphanKeys.length > 0) {
  }

  // CAS SPÉCIAL : Grammaire
  if (category === 'Grammaire') {
    // Utiliser un Map pour garantir l'unicité
    const uniqueGrammarWords = new Map<string, [string, WordData]>();
    const masteredWords = new Set<string>();
    
    // ✅ NOUVEAU: D'abord, identifier les mots maîtrisés
    const revisionHistory = loadSessionHistory();
    const grammarHistory = revisionHistory.filter(h => h.category === 'Grammaire');
    
    // Grouper par mot et compter les succès consécutifs
    const wordSuccessMap = new Map<string, number>();
    grammarHistory.forEach(record => {
      const word = cleanParentheses(record.word);
      if (!wordSuccessMap.has(word)) {
        // Compter les succès consécutifs pour ce mot
        const wordRecords = grammarHistory
          .filter(h => cleanParentheses(h.word) === word)
          .sort((a, b) => b.timestamp - a.timestamp);
        
        let consecutiveSuccess = 0;
        for (const rec of wordRecords) {
          if (rec.isCorrect) {
            consecutiveSuccess++;
          } else {
            break;
          }
        }
        
        wordSuccessMap.set(word, consecutiveSuccess);
        
        // ✅ Si 5+ succès consécutifs, marquer comme maîtrisé
        if (consecutiveSuccess >= 5) {
          masteredWords.add(word);
    
        }
      }
    });
    
    // 1. PRIORITÉ: Charger depuis userProgress.recentlyLearnedWords
    if (userProgress?.recentlyLearnedWords && Array.isArray(userProgress.recentlyLearnedWords)) {
   
      
      userProgress.recentlyLearnedWords.forEach(item => {
        if (item.category === 'Grammaire' && item.word && item.translation) {
          const cleanWord = cleanParentheses(item.word);
          
          // ✅ NOUVEAU: Ne pas ajouter si le mot est maîtrisé
          if (masteredWords.has(cleanWord)) {
          
            return;
          }
          
          // Vérifier si le mot est dû pour révision
          if (isWordDueForRevision(cleanWord, 'Grammaire')) {
            uniqueGrammarWords.set(cleanWord, [
              cleanWord,
              {
                translation: item.translation || '',
                explanation: item.explanation || '',
                example: item.example || ''
              }
            ]);
          }
        }
      });
      
     
    }
    
    // 2. FALLBACK: localStorage (pour compatibilité)
    // Seulement si on n'a pas assez de mots depuis userProgress
    if (uniqueGrammarWords.size === 0) {
   
      
      const storageKey = `grammar-progress-${languageCode}`;
      try {
        const savedProgress = localStorage.getItem(storageKey);
        if (savedProgress) {
          const progress = JSON.parse(savedProgress) as GrammarProgress[];
          const allGrammarWords = progress.flatMap((p: GrammarProgress) => p.masteredWords);
          
          allGrammarWords.forEach(wordData => {
            const cleanWord = cleanParentheses(wordData.word);
            
            // ✅ NOUVEAU: Ne pas ajouter si le mot est maîtrisé ou déjà présent
            if (masteredWords.has(cleanWord) || uniqueGrammarWords.has(cleanWord)) {
              return;
            }
            
            if (isWordDueForRevision(cleanWord, 'Grammaire')) {
              uniqueGrammarWords.set(cleanWord, [
                cleanWord,
                {
                  translation: wordData.data.translation,
                  explanation: wordData.data.explanation || '',
                  example: wordData.data.example || ''
                }
              ]);
            }
          });
        }
      } catch (error) {
}
    }
    
    const grammarWordsArray = Array.from(uniqueGrammarWords.values());
    return grammarWordsArray;
  }
  
  // Pour les mots de vocabulaire normaux
  const categoryWords = languageData.categories[category];
  if (!categoryWords) {
    return [];
  }

  const uniqueWords = new Map<string, [string, WordData]>();
  const masteredWords = new Set<string>();
  
  // ✅ NOUVEAU: Identifier les mots maîtrisés pour cette catégorie
  const revisionHistory = loadSessionHistory();
  const categoryHistory = revisionHistory.filter(h => h.category === category);
  
  categoryHistory.forEach(record => {
    const word = cleanParentheses(record.word);
    
    // Compter les succès consécutifs
    const wordRecords = categoryHistory
      .filter(h => cleanParentheses(h.word) === word)
      .sort((a, b) => b.timestamp - a.timestamp);
    
    let consecutiveSuccess = 0;
    for (const rec of wordRecords) {
      if (rec.isCorrect) {
        consecutiveSuccess++;
      } else {
        break;
      }
    }
    
    if (consecutiveSuccess >= 5) {
      masteredWords.add(word);
    }
  });
  
  // ✅ PRIORITÉ 1: Charger depuis userProgress.recentlyLearnedWords
  if (userProgress?.recentlyLearnedWords && Array.isArray(userProgress.recentlyLearnedWords)) {
   
    
    userProgress.recentlyLearnedWords.forEach(item => {
      if (item.category === category) {
        const cleanWord = cleanParentheses(item.word);
        
        // ✅ NOUVEAU: Ne pas ajouter si maîtrisé
        if (masteredWords.has(cleanWord)) {
          return;
        }
        
        // Vérifier si le mot existe dans les données de la langue
        const wordExists = Object.keys(categoryWords).some(key => {
          if (key.includes('_')) return false;
          const cleanKey = cleanParentheses(key);
          return cleanKey === cleanWord || key === item.word;
        });
        
        if (wordExists && isWordDueForRevision(cleanWord, category)) {
          // Trouver les données complètes du mot
          const matchingEntry = Object.entries(categoryWords).find(([key]) => {
            if (key.includes('_')) return false;
            const cleanKey = cleanParentheses(key);
            return cleanKey === cleanWord || key === item.word;
          });
          
          if (matchingEntry && !uniqueWords.has(cleanWord)) {
            uniqueWords.set(cleanWord, [cleanWord, matchingEntry[1] as WordData]);
          }
        }
      }
    });
   
  }
  
  // 2. FALLBACK: localStorage (pour compatibilité)
  Object.entries(categoryWords)
    .filter(([key]) => !key.includes('_'))
    .forEach(([word, data]) => {
      const cleanWord = cleanParentheses(word);
      
      // ✅ NOUVEAU: Ne pas ajouter si maîtrisé ou déjà présent
      if (masteredWords.has(cleanWord) || uniqueWords.has(cleanWord)) {
        return;
      }
      
      if (isWordDueForRevision(cleanWord, category) && !uniqueWords.has(cleanWord)) {
        uniqueWords.set(cleanWord, [cleanWord, data as WordData]);
      }
    });
  
  const normalWordsArray = Array.from(uniqueWords.values());
  return normalWordsArray;
}, [
  languageCode,
  languageData.categories,
  isWordDueForRevision,
  grammarType,
  loadSessionHistory,
  userProgress,
  revisionProgress
]);

  // Catégories ayant des mots à réviser — mémoïsées pour éviter les recalculs multiples par render
  const filteredCategories = useMemo(() => {
    return Object.keys(languageData.categories)
      .filter(category => getDueWordsForCategory(category).length > 0);
  }, [languageData.categories, getDueWordsForCategory]);

  // Génération d'exercices mixtes avec tests multiples par mot
  const generateMixedExercises = useCallback((words: [string, WordData][]): RevisionExercise[] => {
    // Validation préalable
    if (words.length === 0) {
return [];
    }

    const exercises: RevisionExercise[] = [];
    const shuffledWords = shuffleArray([...words]);
    
    // Fonction pour créer un pool global d'options pour les mots
    const createWordOptionsPool = (excludeWord: string): string[] => {
      const allWords: string[] = [];
      
      // 1. Mots de la catégorie actuelle
      const currentCategoryWords = languageData.categories[selectedCategory || ''];
      if (currentCategoryWords && typeof currentCategoryWords === 'object') {
        Object.keys(currentCategoryWords as Record<string, WordData>)
          .filter(w => !w.includes('_'))
          .forEach(w => {
            const cleanW = cleanParentheses(w);
            if (cleanW !== excludeWord && !allWords.includes(cleanW)) {
              allWords.push(cleanW);
            }
          });
      }
      
      // 2. Si pas assez de mots, ajouter des mots d'autres catégories
      if (allWords.length < 10) {
        Object.entries(languageData.categories)
          .filter(([catName]) => catName !== selectedCategory && catName !== 'Grammaire')
          .forEach(([, categoryWords]) => {
            if (typeof categoryWords === 'object' && categoryWords !== null) {
              Object.keys(categoryWords as Record<string, WordData>)
                .filter(w => !w.includes('_'))
                .forEach(w => {
                  const cleanW = cleanParentheses(w);
                  if (cleanW !== excludeWord && !allWords.includes(cleanW) && allWords.length < 50) {
                    allWords.push(cleanW);
                  }
                });
            }
          });
      }
      
      return shuffleArray(allWords);
    };
    
    // Fonction pour créer un pool d'options de traductions (localisées, pour
    // rester cohérentes avec le mot correct passé par l'appelant).
    const createTranslationOptionsPool = (excludeTranslation: string): string[] => {
      const allTranslations: string[] = [];

      // 1. Traductions de la catégorie actuelle
      const currentCategoryWords = languageData.categories[selectedCategory || ''];
      if (currentCategoryWords && typeof currentCategoryWords === 'object') {
        Object.values(currentCategoryWords as Record<string, WordData>)
          .forEach(data => {
            if (typeof data === 'object' && data !== null && 'translation' in data) {
              const translation = getWordTranslation(data, i18n.language);
              if (translation !== excludeTranslation && !allTranslations.includes(translation)) {
                allTranslations.push(translation);
              }
            }
          });
      }

      // 2. Si pas assez de traductions, ajouter d'autres catégories
      if (allTranslations.length < 10) {
        Object.entries(languageData.categories)
          .filter(([catName]) => catName !== selectedCategory && catName !== 'Grammaire')
          .forEach(([, categoryWords]) => {
            if (typeof categoryWords === 'object' && categoryWords !== null) {
              Object.values(categoryWords as Record<string, WordData>)
                .forEach(data => {
                  if (typeof data === 'object' && data !== null && 'translation' in data) {
                    const translation = getWordTranslation(data, i18n.language);
                    if (translation !== excludeTranslation && !allTranslations.includes(translation) && allTranslations.length < 50) {
                      allTranslations.push(translation);
                    }
                  }
                });
            }
          });
      }

      return shuffleArray(allTranslations);
    };
    
    // 1. Créer les exercices QCM pour chaque mot
    shuffledWords.forEach((wordPair, wordIndex) => {
      const [word, wordData] = wordPair;
      const cleanWord = cleanParentheses(word);
      
      // QCM CLASSIQUE : Français → Langue cible
      const wordOptionsPool = createWordOptionsPool(cleanWord);
      let qcmOptions: string[];
      
      if (wordOptionsPool.length >= 3) {
        // Assez d'options disponibles
        const wrongOptions = wordOptionsPool.slice(0, 3);
        qcmOptions = shuffleArray([cleanWord, ...wrongOptions]);
      } else {
        // Pas assez d'options - utiliser ce qu'on a + fallback
        const availableOptions = wordOptionsPool.slice(0, 2);
        const fallbackCount = 3 - availableOptions.length;
        const fallbacks = [`Option ${String.fromCharCode(65 + availableOptions.length + 1)}`, 
                          `Option ${String.fromCharCode(65 + availableOptions.length + 2)}`].slice(0, fallbackCount);
        qcmOptions = shuffleArray([cleanWord, ...availableOptions, ...fallbacks]);
      }
      
      exercises.push({
        id: `qcm-${wordIndex}`,
        type: 'qcm',
        word,
        wordData,
        options: qcmOptions,
        variant: 'classic'
      });
      
      // QCM INVERSÉ : Langue cible → traduction
      const correctTranslationForReverse = getWordTranslation(wordData, i18n.language);
      const translationOptionsPool = createTranslationOptionsPool(correctTranslationForReverse);
      let frenchOptions: string[];

      if (translationOptionsPool.length >= 3) {
        // Assez de traductions disponibles
        const wrongTranslations = translationOptionsPool.slice(0, 3);
        frenchOptions = shuffleArray([correctTranslationForReverse, ...wrongTranslations]);
      } else {
        // Pas assez de traductions - utiliser ce qu'on a + fallback
        const availableTranslations = translationOptionsPool.slice(0, 2);
        const fallbackCount = 3 - availableTranslations.length;
        const fallbacks = [`Traduction ${String.fromCharCode(65 + availableTranslations.length + 1)}`,
                          `Traduction ${String.fromCharCode(65 + availableTranslations.length + 2)}`].slice(0, fallbackCount);
        frenchOptions = shuffleArray([correctTranslationForReverse, ...availableTranslations, ...fallbacks]);
      }
      
      exercises.push({
        id: `qcm-reverse-${wordIndex}`,
        type: 'qcm',
        word,
        wordData,
        options: frenchOptions,
        variant: 'reverse'
      });
    });
    
    // 2. Ajouter UN SEUL exercice d'appariement s'il y a assez de mots
    if (words.length >= 2) {
      const groupSize = Math.min(5, words.length);
      const groupWords = shuffledWords.slice(0, groupSize);

      // Dédupliquer par traduction : si deux mots ont la même traduction,
      // garder uniquement le premier pour éviter les doublons dans le matching.
      const seenTranslations = new Set<string>();
      const dedupedGroupWords = groupWords.filter(([, data]) => {
        const translation = getWordTranslation(data, i18n.language);
        if (seenTranslations.has(translation)) return false;
        seenTranslations.add(translation);
        return true;
      });

      // S'assurer qu'on a au moins 2 paires uniques pour l'exercice d'appariement
      if (dedupedGroupWords.length >= 2) {
        // shuffledTranslations (colonne de droite) est construit ailleurs en
        // recopiant/mélangeant ces mêmes pairs — localiser translation ici
        // suffit à garder la colonne de droite ET le checkMatch() (qui compare
        // pair.translation à la valeur choisie/déposée) mutuellement cohérents.
        const pairs: WordPair[] = dedupedGroupWords.map((wordPair, pairIndex) => ({
          id: `pair-matching-${pairIndex}`,
          word: cleanParentheses(wordPair[0]),
          translation: getWordTranslation(wordPair[1], i18n.language),
          audio: wordPair[1].audio,
          matched: false
        }));

        // Utiliser le premier mot comme mot principal pour les statistiques
        exercises.push({
          id: 'matching-session',
          type: 'matching',
          word: shuffledWords[0][0],
          wordData: shuffledWords[0][1],
          pairs,
          targetWord: cleanParentheses(shuffledWords[0][0])
        });
      }
    }
    
    // 3. Mélanger tous les exercices pour une session variée
    const finalExercises = shuffleArray(exercises);
    
    return finalExercises;
  }, [languageData.categories, selectedCategory, i18n.language]);

  // Fonction de sauvegarde sécurisée
  const handleSessionSave = useCallback(async () => {
    if (isSaving || hasSaved || tempSessionData.length === 0) {
      return;
    }

    setIsSaving(true);
try {
      const dataToSave = [...tempSessionData];

      // Grouper les données par mot pour éviter les doublons
      const wordPerformances = new Map<string, {
        attempts: number;
        successes: number;
        exerciseTypes: Set<string>;
      }>();

      dataToSave.forEach(wordInfo => {
        const existing = wordPerformances.get(wordInfo.word) || {
          attempts: 0,
          successes: 0,
          exerciseTypes: new Set()
        };

        existing.attempts += 1;
        if (wordInfo.isCorrect) existing.successes += 1;
        existing.exerciseTypes.add(wordInfo.exerciseType);

        wordPerformances.set(wordInfo.word, existing);
      });

      // Mettre à jour la progression pour chaque mot unique
      for (const [word, performance] of wordPerformances) {
        const successRate = performance.successes / performance.attempts;
        const isCorrect = successRate >= 0.75 && performance.attempts >= 1;
// Récupérer les données du mot (translation/audio) depuis wordDataMap
        const wordEntry = wordDataMap[selectedCategory || '']?.[word];

        await updateRevisionProgress(
          word,
          isCorrect,
          selectedCategory || '',
          {
            ...(isGrammarMode ? {
              isGrammar: true,
              subCategory: grammarSubCategory || undefined,
              grammarType
            } : {}),
            translation: wordEntry?.translation,
            audio: wordEntry?.audio
          }
        );
      }
      
      // Finaliser la session
      await finishSession({
        showStats: true,
        lastWordInfo: dataToSave[dataToSave.length - 1]
      });

      onSessionComplete?.();

      // Record daily activity for heatmap
      try {
        const _d = new Date();
        const todayKey = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, '0')}-${String(_d.getDate()).padStart(2, '0')}`;
        const actKey = `${languageCode}-daily-activity`;
        const raw = localStorage.getItem(actKey);
        const map: Record<string, number> = raw ? JSON.parse(raw) : {};
        map[todayKey] = (map[todayKey] ?? 0) + wordPerformances.size;
        localStorage.setItem(actKey, JSON.stringify(map));
      } catch {}

      setHasSaved(true);

    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  }, [
    tempSessionData,
    selectedCategory,
    isGrammarMode,
    grammarSubCategory,
    grammarType,
    updateRevisionProgress,
    finishSession,
    wordDataMap,
    isSaving,
    hasSaved,
    languageCode,
  ]);

  // Gestion du bouton retour
  const handleBackButton = useCallback(() => {
    // Seule la phase d'exercice nécessite une confirmation, pas le résumé
    if (sessionPhase === 'exercise') {
      setShowExitConfirmation(true);
    } else {
      onBackToMenu();
    }
  }, [sessionPhase, onBackToMenu]);

  // Gestion de la confirmation de sortie
  const handleExitConfirm = useCallback(() => {
    setShowExitConfirmation(false);
    setTempSessionData([]);
    setIsSaving(false);
    setHasSaved(false);
    setSessionPhase('category-selection');
    setCurrentExercises([]);
    setCurrentExerciseIndex(0);
    setCurrentExercise(null);
    setSelectedAnswer(null);
    setFeedback(null);
    setIsTransitioning(false);
    setShuffledTranslations([]); // Reset des traductions mélangées
    onBackToMenu();
  }, [onBackToMenu]);

  // Sélection de catégorie
  const handleCategorySelect = useCallback((category: string, isGrammar: boolean = false) => {
    const dueWords = getDueWordsForCategory(category);
    
    setSelectedCategory(category);
    setCurrentWords(dueWords);
    setIsGrammarMode(isGrammar);
    
    // Fermer automatiquement la liste des mots si elle était ouverte
    setShowWordsList(false);
    
    if (isGrammar) {
      setGrammarType('rule');
      setGrammarSubCategory(null);
    }
    
    const newWordDataMap: Record<string, Record<string, WordData>> = {
      [category]: Object.fromEntries(dueWords.map(([word, data]) => [word, data]))
    };
    setWordDataMap(newWordDataMap);
    setShowWordsList(false);
    setShowCategoryModal(true);
  }, [getDueWordsForCategory]);

  const closeCategoryModal = useCallback(() => {
    setShowCategoryModal(false);
    setSelectedCategory(null);
    setCurrentWords([]);
    setShowWordsList(false);
  }, []);

  // Fonction pour basculer l'affichage de la liste des mots
  const toggleWordsList = useCallback(() => {
    setShowWordsList(prev => !prev);
  }, []);

  // Démarrage de la révision avec exercices mixtes multiples
  const startRevision = useCallback(() => {
    if (currentWords.length === 0) {
      setFeedback(t('revision.noWordsAvailable'));
      return;
    }
  
    // Limiter à 4-6 mots pour permettre plusieurs tests par mot
    const maxWords = Math.min(5, currentWords.length);
    const selectedWords = shuffleArray([...currentWords]).slice(0, maxWords);
    
    // Générer les exercices mixtes (2-3 exercices par mot)
    const exercises = generateMixedExercises(selectedWords);
    
  

    
    setCurrentExercises(exercises);
    setCurrentExerciseIndex(0);
    setCurrentExercise(exercises[0] || null);
    setSessionPhase('exercise');
    
    // Réinitialiser TOUS les états de feedback
    setSelectedAnswer(null);
    setFeedback(null);
    setIsTransitioning(false);
    setIsSaving(false);
    setHasSaved(false);
    setTempSessionData([]);
    
    // Fermer la liste des mots lors du démarrage
    setShowWordsList(false);
    
    // Initialiser l'ordre mélangé pour le premier exercice d'appariement
    if (exercises[0]?.type === 'matching' && exercises[0]?.pairs) {
      setShuffledTranslations(shuffleArray([...exercises[0].pairs]));
      setMatches(new Map());
      setSelectedWord(null);
      setSelectedTranslation(null);
      setIncorrectMatches(new Set());
      setMatchAttempts(new Map());
    }
  }, [currentWords, generateMixedExercises]);

  // useEffect pour initialiser l'ordre mélangé des traductions
  useEffect(() => {
    if (currentExercise?.type === 'matching' && currentExercise.pairs) {
      setShuffledTranslations(shuffleArray([...currentExercise.pairs]));
    }
  }, [currentExercise?.id, currentExercise?.type]);

  // Gestion des réponses QCM améliorée
  const handleQCMAnswer = useCallback(async (answer: string) => {
    if (!answer || isTransitioning || !currentExercise) return;
    
    setIsTransitioning(true);
    const cleanWord = cleanParentheses(currentExercise.word);
    
    // Déterminer la bonne réponse selon la variante — doit rester cohérent
    // avec le pool localisé généré dans generateMixedExercises.
    const correctAnswer = currentExercise.variant === 'reverse'
      ? getWordTranslation(currentExercise.wordData, i18n.language)
      : cleanWord;
    
    const isCorrect = answer === correctAnswer;
    
    setSelectedAnswer(answer);
    setFeedback(isCorrect
      ? t('grammar.correct')
      : t('revision.incorrectAnswerIs', { answer: correctAnswer })
    );
  
    if (currentExercise.wordData.audio) {
      try {
        await playWord(currentExercise.wordData.audio);
      } catch (error) {
}
    }
  
    // Ajouter seulement à tempSessionData, PAS d'appel direct à updateRevisionProgress
    setTempSessionData(prev => {
      // Vérifier si ce résultat n'existe pas déjà
      const exists = prev.some(item => 
        item.word === cleanWord &&
        item.exerciseType === (currentExercise.variant === 'reverse' ? 'qcm-reverse' : 'qcm') &&
        Math.abs(item.timestamp - Date.now()) < 5000 // Moins de 5 secondes d'écart
      );
      
      if (exists) {
        return prev; // Ne pas ajouter de doublon
      }
      
      return [...prev, {
        word: cleanWord,
        isCorrect,
        category: selectedCategory || '',
        timestamp: Date.now(),
        exerciseType: currentExercise.variant === 'reverse' ? 'qcm-reverse' : 'qcm',
        attempts: 1
      }];
    });
    
    onWordRevised?.(cleanWord, isCorrect);

    setSessionStats(prev => ({
      ...prev,
      correctAnswers: prev.correctAnswers + (isCorrect ? 1 : 0),
      totalAttempts: prev.totalAttempts + 1,
      grammarProgress: isGrammarMode ? {
        ...prev.grammarProgress,
        [grammarType]: prev.grammarProgress[grammarType] + (isCorrect ? 1 : 0)
      } : prev.grammarProgress
    }));

    // Attendre, puis nettoyer TOUS les états avant de passer au suivant
    setTimeout(() => {
      // 1. D'abord, nettoyer tous les états de la question actuelle
      setSelectedAnswer(null);
      setFeedback(null);  
      setIsTransitioning(false);
      
      // 2. Ensuite, passer à l'exercice suivant
      if (currentExerciseIndex < currentExercises.length - 1) {
        const nextIndex = currentExerciseIndex + 1;
        setCurrentExerciseIndex(nextIndex);
        setCurrentExercise(currentExercises[nextIndex]);
        
        // 3. Initialiser les états pour l'exercice suivant
        if (currentExercises[nextIndex]?.type === 'matching') {
          setMatches(new Map());
          setSelectedWord(null);
          setSelectedTranslation(null);
          setIncorrectMatches(new Set());
          setMatchAttempts(new Map());
          // Initialiser l'ordre mélangé pour le nouvel exercice d'appariement
          if (currentExercises[nextIndex]?.pairs) {
            setShuffledTranslations(shuffleArray([...currentExercises[nextIndex].pairs!]));
          }
        }
      } else {
        // Session terminée
        setSessionPhase('complete');
      }
    }, 1500);
  }, [
    currentExercise,
    isTransitioning,
    selectedCategory,
    onWordRevised,
    isGrammarMode,
    grammarType,
    playWord,
    currentExerciseIndex,
    currentExercises,
    i18n.language,
    t
  ]);

  // Gestion de l'appariement (adaptée)
  const handleWordSelect = useCallback((word: string) => {
    if (matches.has(word)) return;
    setSelectedWord(prev => prev === word ? null : word);
    setIncorrectMatches(prev => {
      const newSet = new Set(prev);
      newSet.delete(word);
      return newSet;
    });
  }, [matches]);

  const handleTranslationSelect = useCallback((translation: string) => {
    if (Array.from(matches.values()).includes(translation)) return;
    setSelectedTranslation(prev => prev === translation ? null : translation);
    setIncorrectMatches(prev => {
      const newSet = new Set(prev);
      newSet.delete(translation);
      return newSet;
    });
  }, [matches]);

  // Améliorer la gestion du feedback dans l'appariement
  const checkMatch = useCallback((word: string, translation: string) => {
    if (!currentExercise || currentExercise.type !== 'matching') return;
    
    const pair = currentExercise.pairs?.find(p => p.word === word);
    const isCorrect = pair?.translation === translation;
    
    const currentAttempts = matchAttempts.get(word) || 0;
    setMatchAttempts(prev => new Map(prev).set(word, currentAttempts + 1));
    
    if (isCorrect) {
      setMatches(prev => new Map(prev).set(word, translation));
      setSelectedWord(null);
      setSelectedTranslation(null);
      
      if (pair?.audio) {
        playWord(pair.audio);
      }
      
      // Enregistrer le résultat réussi pour ce mot (pas seulement le targetWord)
      setTempSessionData(prev => [...prev, {
        word: word,
        isCorrect: true,
        category: selectedCategory || '',
        timestamp: Date.now(),
        exerciseType: 'matching',
        attempts: currentAttempts + 1
      }]);
      
      // Vérifier si tous les appariements sont terminés
      if (currentExercise.pairs && matches.size + 1 >= currentExercise.pairs.length) {
        setTimeout(() => {
          // Nettoyer le feedback avant de passer au suivant
          setFeedback(null);
          setSelectedAnswer(null);
          
          if (currentExerciseIndex < currentExercises.length - 1) {
            const nextIndex = currentExerciseIndex + 1;
            setCurrentExerciseIndex(nextIndex);
            setCurrentExercise(currentExercises[nextIndex]);
            
            // Réinitialiser les états d'appariement
            setMatches(new Map());
            setSelectedWord(null);
            setSelectedTranslation(null);
            setIncorrectMatches(new Set());
            setMatchAttempts(new Map());
            // Réinitialiser l'ordre mélangé pour le nouvel exercice
            if (currentExercises[nextIndex]?.type === 'matching' && currentExercises[nextIndex]?.pairs) {
              setShuffledTranslations(shuffleArray([...currentExercises[nextIndex].pairs!]));
            }
          } else {
            setSessionPhase('complete');
          }
        }, 1000);
      }
    } else {
      setIncorrectMatches(prev => new Set([...prev, word, translation]));
      
      // Enregistrer l'échec pour ce mot (tous les mots, pas seulement le targetWord)
      setTempSessionData(prev => [...prev, {
        word: word,
        isCorrect: false,
        category: selectedCategory || '',
        timestamp: Date.now(),
        exerciseType: 'matching',
        attempts: currentAttempts + 1
      }]);
      
      setTimeout(() => {
        setSelectedWord(null);
        setSelectedTranslation(null);
        setIncorrectMatches(prev => {
          const newSet = new Set(prev);
          newSet.delete(word);
          newSet.delete(translation);
          return newSet;
        });
      }, 1000);
    }
  }, [currentExercise, matches, matchAttempts, playWord, selectedCategory, currentExerciseIndex, currentExercises]);

  useEffect(() => {
    if (selectedWord && selectedTranslation) {
      checkMatch(selectedWord, selectedTranslation);
    }
  }, [selectedWord, selectedTranslation, checkMatch]);

  // Drag & Drop pour l'appariement
  const handleDragStart = useCallback((
    e: any,
    id: string,
    type: 'word' | 'translation',
    content: string,
    audio?: string
  ) => {
    const draggedData: DraggedItem = { id, type, content, audio };
    setDraggedItem(draggedData);
    e.dataTransfer.setData('application/json', JSON.stringify(draggedData));
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: any, targetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropZoneActive(targetId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropZoneActive(null);
  }, []);

  const handleDrop = useCallback((e: any, targetContent: string, targetType: 'word' | 'translation') => {
    e.preventDefault();
    setDropZoneActive(null);
    
    try {
      const draggedData: DraggedItem = JSON.parse(e.dataTransfer.getData('application/json'));
      
      if (draggedData.type === targetType) return;
      
      const word = draggedData.type === 'word' ? draggedData.content : targetContent;
      const translation = draggedData.type === 'translation' ? draggedData.content : targetContent;
      
      checkMatch(word, translation);
    } catch (error) {
}
    
    setDraggedItem(null);
  }, [checkMatch]);

  const handlePlayAudio = useCallback((word: string) => {
    if (!currentExercise || currentExercise.type !== 'matching') return;
    const pair = currentExercise.pairs?.find(p => p.word === word);
    if (pair?.audio) {
      playWord(pair.audio);
    }
  }, [currentExercise, playWord]);

  // useEffect de nettoyage lors du changement d'exercice
  useEffect(() => {
    // Réinitialiser le feedback à chaque changement d'exercice
    setSelectedAnswer(null);
    setFeedback(null);
    setIsTransitioning(false);
  }, [currentExerciseIndex, currentExercise?.id]);

  // Contrôler la sauvegarde avec useEffect
  // IMPORTANT: Ne PAS utiliser setTimeout ici. La fonction de cleanup du useEffect
  // annulerait le timeout si l'utilisateur clique "Retour aux catégories" avant les 100ms,
  // empêchant la sauvegarde. Appeler handleSessionSave immédiatement garantit que
  // dataToSave = [...tempSessionData] est capturé AVANT que handleResetSession ne vide
  // tempSessionData. Les vérifications isSaving/hasSaved évitent les appels multiples.
  useEffect(() => {
    if (sessionPhase === 'complete' && tempSessionData.length > 0 && !hasSaved && !isSaving) {
      handleSessionSave();
    }
  }, [sessionPhase, tempSessionData.length, hasSaved, isSaving, handleSessionSave]);

  // Nettoyer le feedback lors du reset de session
  const handleResetSession = useCallback(() => {
    // Nettoyer TOUS les états de feedback
    setSelectedAnswer(null);
    setFeedback(null);
    setIsTransitioning(false);

    // Nettoyer les autres états
    setTempSessionData([]);
    setIsSaving(false);
    setHasSaved(false);
    resetSession();
    setSessionPhase('category-selection');
    setCurrentExercises([]);
    setCurrentExerciseIndex(0);
    setCurrentExercise(null);

    // Désélectionner la catégorie pour masquer les boutons "Voir la liste" / "Commencer la session"
    setSelectedCategory(null);
    setCurrentWords([]);

    // Fermer la liste des mots lors du reset
    setShowWordsList(false);
    
    // Nettoyer les états d'appariement
    setMatches(new Map());
    setSelectedWord(null);
    setSelectedTranslation(null);
    setIncorrectMatches(new Set());
    setMatchAttempts(new Map());
    setShuffledTranslations([]); // Reset des traductions mélangées
  }, [resetSession]);

  

  // useEffect pour migration et nettoyage au montage
  useEffect(() => {
    const migrationKey = `${languageCode}-complete-migration-v2`;
    
    if (!localStorage.getItem(migrationKey)) {
     
      
      // 1. Migration forcée
      forceMigrationOnStart();
      
      // 2. Synchronisation depuis LearnMode
      setTimeout(() => {
        syncFromLearnMode();
      }, 500);
      
      // 3. Nettoyage des doublons
      setTimeout(() => {
        checkAndCleanCrossCategory();
      }, 1000);
      
      // Marquer comme terminé
      localStorage.setItem(migrationKey, 'true');
    
    }
  }, [languageCode, forceMigrationOnStart, syncFromLearnMode, checkAndCleanCrossCategory]);

  // Surveiller les changements de localStorage
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key?.includes(languageCode) && event.key?.includes('learnedWords')) {
    
        // Forcer le recalcul
        setTimeout(() => {
          if (selectedCategory) {
            const dueWords = getDueWordsForCategory(selectedCategory);
            setCurrentWords(dueWords);
    
          }
        }, 100);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [languageCode, selectedCategory, getDueWordsForCategory]);

  // Fonction de nettoyage lors du démontage
  useEffect(() => {
    return () => {
      // Nettoyage au démontage pour éviter les fuites mémoire
      setTempSessionData([]);
      setIsSaving(false);
      setHasSaved(false);
      setShuffledTranslations([]);
    };
  }, []);

  // Condition d'affichage renforcée pour le feedback
  const shouldShowFeedback = feedback && !isTransitioning && selectedAnswer;

  // Calculer le nombre réel de mots dus en sommant par catégorie (même logique que les cartes affichées)
  const dueWordsCount = useMemo(() => {
    return filteredCategories.reduce((total, category) => {
      return total + getDueWordsForCategory(category).length;
    }, 0);
  }, [filteredCategories, getDueWordsForCategory]);

  // Remonter le nombre exact vers le parent (badge MainMenu) dès qu'il change
  useEffect(() => {
    if (!isLoadingProgress) {
      onDueWordsCountChange?.(dueWordsCount);
    }
  }, [dueWordsCount, isLoadingProgress, onDueWordsCountChange]);

  // Les comptes par catégorie nécessitent userProgress.recentlyLearnedWords.
  // On attend que les deux sources soient prêtes avant d'afficher les cartes.
  const isUserProgressReady = !!(userProgress?.recentlyLearnedWords?.length);
  const isReadyToShow = !isLoadingProgress && (isUserProgressReady || dueWordsCount === 0);

  return (
    <div
      className={`revision-mode-wrapper ${theme === 'dark' ? 'dark-mode' : ''} ${showStatsModal ? 'modal-open' : ''}`}
      data-theme={theme}
    >
      <TribalBorder />
      {sessionPhase === 'exercise' && (
        <FloatingBackButton
          onBack={handleBackButton}
          position="top-left"
          variant="minimal"
        />
      )}
      <div className="revision-mode">
        {/* Phase de sélection des catégories */}
        {sessionPhase === 'category-selection' && (
          <div className="rev-sel-screen" data-theme={theme}>
            {/* Header */}
            <div className="rev-sel-header">
              <div className="rev-sel-header__arch">
                <span className="rev-sel-header__deco">◇ · ◇</span>
                <h1 className="rev-sel-header__title">{languageData.name}</h1>
                <p className="rev-sel-header__subtitle">{t('revision.title')}</p>
              </div>
              {dueWordsCount > 0 && (
                <p className="rev-sel-due-count">{t('revision.dueCount', { count: dueWordsCount })}</p>
              )}
            </div>

            {!isReadyToShow ? (
              <div className="rev-sel-loading">
                <div className="rev-sel-loading__spinner" />
                <p>{t('revision.loadingRevisions')}</p>
              </div>
            ) : dueWordsCount === 0 ? (
              <div className="rev-sel-empty">
                <div className="rev-sel-empty__icon"><PartyPopper size={40} strokeWidth={1.5} /></div>
                <h2 className="rev-sel-empty__title">{t('revision.allUpToDate')}</h2>
                <p className="rev-sel-empty__text">{t('revision.comeBackLater')}</p>
                {onStartLearning && (
                  <button className="rev-sel-empty__cta" onClick={onStartLearning}>
                    <EmojiText text={t('revision.goLearn') as string} />
                  </button>
                )}
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="rev-sel-empty">
                <div className="rev-sel-empty__icon"><Sparkles size={40} strokeWidth={1.5} /></div>
                <h2 className="rev-sel-empty__title">{t('revision.allRevisionsUpToDate')}</h2>
                <p className="rev-sel-empty__text">{t('revision.learnNewWords')}</p>
                {onStartLearning && (
                  <button className="rev-sel-empty__cta" onClick={onStartLearning}>
                    <EmojiText text={t('revision.startLearning') as string} />
                  </button>
                )}
              </div>
            ) : (
              <div className="rev-sel-body">
                <p className="rev-sel-body__hint">{t('revision.chooseCategory')}</p>

                <div className="rev-sel-grid">
                  {filteredCategories.map((categoryName, idx) => {
                    const dueWords = getDueWordsForCategory(categoryName);
                    const isGrammar = categoryName === 'Grammaire';
                    const isSelected = selectedCategory === categoryName;
                    return (
                      <motion.div
                        key={categoryName}
                        className={`rev-sel-card ${isSelected ? 'rev-sel-card--selected' : ''}`}
                        onClick={() => handleCategorySelect(categoryName, isGrammar)}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.06 }}
                      >
                        <div className="rev-sel-card__arch">
                          <span className="rev-sel-card__emoji">{dueWords.length}</span>
                        </div>
                        <div className="rev-sel-card__body">
                          <h3 className="rev-sel-card__name">{displayCategoryLabel(categoryName, t)}</h3>
                          <span className="rev-sel-card__count">{t('revision.wordsCount', { count: dueWords.length })}</span>
                        </div>
                        {isSelected && <Check className="rev-sel-card__check" size={13} strokeWidth={3} />}
                      </motion.div>
                    );
                  })}
                </div>

                {/* Proverbe du jour */}
                {(() => {
                  const proverb = summaryProverb;
                  if (!proverb) return null;
                  return (
                    <div className="rev-sel-proverb">
                      <p className="rev-sel-proverb__label">{t('revision.proverbOfDay')}</p>
                      {proverb.illustration && (
                        <p className="rev-sel-proverb__glyphs">{proverb.illustration}</p>
                      )}
                      <p className="rev-sel-proverb__text">« {proverb.text} »</p>
                      {proverb.translation && (
                        <p className="rev-sel-proverb__trans">{getWordTranslation(proverb, i18n.language)}</p>
                      )}
                    </div>
                  );
                })()}

                {/* Bouton Prochaines révisions — inline sous le proverbe */}
                <motion.button
                  className="show-stats-button"
                  onClick={() => setShowStatsModal(true)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    opacity: showStatsModal ? 0.3 : 1,
                    pointerEvents: showStatsModal ? 'none' : 'auto'
                  }}
                >
                  <RotateCw size={18} />
                  <span>{t('revision.upcomingRevisions')}</span>
                </motion.button>
              </div>
            )}
          </div>
        )}

        {/* Phase d'exercice avec affichage optimisé */}
        {/* Phase d'exercice avec affichage optimisé */}
        {sessionPhase === 'exercise' && currentExercise && (
          <div className="rev-exercise-screen" data-theme={theme}>
            {/* Top bar */}
            <div className="rev-ex-topbar">
              <div className="rev-ex-topbar__track">
                <div
                  className="rev-ex-topbar__fill"
                  style={{ width: `${Math.max(4, (currentExerciseIndex / currentExercises.length) * 100)}%` }}
                />
              </div>
              <span className="rev-ex-topbar__count">{currentExerciseIndex + 1}/{currentExercises.length}</span>
            </div>
            <div className="rev-ex-cat-badge">{selectedCategory ? displayCategoryLabel(selectedCategory, t) : selectedCategory}</div>

            {currentExercise.type === 'qcm' ? (
              <div className="rev-qcm">
                {/* Question arch card */}
                <div className="rev-qcm__card">
                  <div className="rev-qcm__dome">
                    <p className="rev-qcm__label">
                      {currentExercise.variant === 'reverse' ? t('revision.translateIntoFrench') : t('revision.translate')}
                    </p>
                    <h3 className={`rev-qcm__word${languageCode === 'ka' && currentExercise.variant === 'reverse' ? ' rev-qcm__word--glyphs' : ''}`}>
                      {currentExercise.variant === 'reverse'
                        ? kamaDisplay(cleanParentheses(currentExercise.word))
                        /* Display-only: the QCM options in this branch are matched against
                           cleanParentheses(currentExercise.word), not against this translation
                           value, so localizing it here doesn't affect answer matching. */
                        : getWordTranslation(currentExercise.wordData, i18n.language)}
                    </h3>

                    <div className="rev-qcm__divider" aria-hidden="true">
                      <span className="rev-qcm__divider-line" />
                      <span className="rev-qcm__divider-diamond" />
                      <span className="rev-qcm__divider-line" />
                    </div>

                    {currentExercise.variant === 'reverse' && hasValidAudio(currentExercise.wordData.audio) && (
                      <button className="rev-qcm__audio" onClick={() => playWord(currentExercise.wordData.audio!)}>
                        <Volume2 size={16} /><span>{t('learnMode.listenButton')}</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Options grid */}
                <div className="rev-qcm__options">
                  {currentExercise.options?.map((option, index) => {
                    const isCorrectAnswer = currentExercise.variant === 'reverse'
                      ? option === getWordTranslation(currentExercise.wordData, i18n.language)
                      : option === cleanParentheses(currentExercise.word);
                    const showCorrect = !!feedback && isCorrectAnswer;
                    const showWrong   = !!feedback && selectedAnswer === option && !isCorrectAnswer;
                    return (
                      <motion.button
                        key={`${option}-${index}`}
                        className={`rev-qcm__opt${showCorrect ? ' rev-qcm__opt--correct' : ''}${showWrong ? ' rev-qcm__opt--wrong' : ''}`}
                        onClick={() => handleQCMAnswer(option)}
                        disabled={!!selectedAnswer}
                        whileHover={!selectedAnswer ? { scale: 1.02 } : undefined}
                        whileTap={!selectedAnswer ? { scale: 0.97 } : undefined}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.07 }}
                      >
                        <span className="rev-qcm__opt-letter">{String.fromCharCode(65 + index)}</span>
                        <span className={`rev-qcm__opt-text${languageCode === 'ka' && currentExercise.variant === 'classic' ? ' rev-qcm__opt-text--glyphs' : ''}`}>
                          {currentExercise.variant === 'classic' ? kamaDisplay(option) : option}
                        </span>
                        {showCorrect && <Check className="rev-qcm__opt-icon rev-qcm__opt-icon--ok" size={16} strokeWidth={3} />}
                        {showWrong   && <X className="rev-qcm__opt-icon rev-qcm__opt-icon--no" size={16} strokeWidth={3} />}
                      </motion.button>
                    );
                  })}
                </div>

                <AnimatePresence>
                  {shouldShowFeedback && (
                    <motion.div
                      className={`rev-qcm__feedback ${feedback.startsWith('Correct') ? 'rev-qcm__feedback--ok' : 'rev-qcm__feedback--no'}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                    >
                      <span>{feedback.startsWith('Correct') ? <CheckCircle size={18} /> : <XCircle size={18} />}</span>
                      <span>{feedback}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              // Test d'appariement avec design optimisé
              <div className="matching-test-container">
                <div className="matching-header">
                  <div className="matching-progress">
                    <span className="progress-text">
                      {t('revision.matchesProgress', { matched: matches.size, total: currentExercise.pairs?.length || 0 })}
                    </span>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ width: `${(matches.size / (currentExercise.pairs?.length || 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="matching-instructions">
                  <div className="instruction-card">
                    <div className="instruction-icon"><Target size={20} strokeWidth={1.75} /></div>
                    <p>{t('revision.matchInstructions')}</p>
                  </div>
                </div>

                <div className="matching-area">
                  <div className="words-column">
                    <div className="column-header">
                      <h4>{languageCode === 'wf' ? 'Wolof' : languageCode === 'la' ? 'Lingala' : t('revision.targetLanguage')}</h4>
                    </div>
                    <div className="items-list">
                      {currentExercise.pairs?.map((pair, index) => {
                        const isMatched = matches.has(pair.word);
                        const isSelected = selectedWord === pair.word;
                        const isIncorrect = incorrectMatches.has(pair.word);
                        
                        return (
                          <motion.div
                            key={`word-${index}`}
                            className={`matching-item word-item ${
                              isMatched ? 'matched' : ''
                            } ${isSelected ? 'selected' : ''} ${
                              isIncorrect ? 'incorrect' : ''
                            } ${dropZoneActive === pair.word ? 'drop-active' : ''}`}
                            onClick={() => !isMatched && handleWordSelect(pair.word)}
                            draggable={!isMatched}
                            onDragStart={(e) => handleDragStart(e, `word-${index}`, 'word', pair.word, pair.audio)}
                            onDragOver={(e) => handleDragOver(e, pair.word)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, pair.word, 'word')}
                            whileHover={!isMatched ? { scale: 1.02 } : undefined}
                            whileTap={!isMatched ? { scale: 0.98 } : undefined}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            layout
                          >
                            <div className="item-content">
                              <span className={`item-text${languageCode === 'ka' ? ' item-text--glyphs' : ''}`}>
                                {kamaDisplay(pair.word)}
                              </span>
                              <div className="item-actions">
                                {isMatched && <CheckCircle size={18} className="match-icon" />}
                                {isIncorrect && <XCircle size={18} className="incorrect-icon" />}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="translations-column">
                    <div className="column-header">
                      <h4>{t('landing.vocabTable.colFrench')}</h4>
                    </div>
                    <div className="items-list">
                      {/* Utilisation de shuffledTranslations au lieu de shuffleArray */}
                      {/* pair.translation sert à la fois de texte affiché et de clé d'identité
                          pour le drag & drop / matching (handleDrop, dropZoneActive, matches...).
                          Déjà localisé à la source (generateMixedExercises), donc affichage et
                          logique de correspondance restent automatiquement cohérents. */}
                      {shuffledTranslations.map((pair, index) => {
                        const isMatched = Array.from(matches.values()).includes(pair.translation);
                        const isSelected = selectedTranslation === pair.translation;
                        const isIncorrect = incorrectMatches.has(pair.translation);
                        
                        return (
                          <motion.div
                            key={`translation-${index}`}
                            className={`matching-item translation-item ${
                              isMatched ? 'matched' : ''
                            } ${isSelected ? 'selected' : ''} ${
                              isIncorrect ? 'incorrect' : ''
                            } ${dropZoneActive === pair.translation ? 'drop-active' : ''}`}
                            onClick={() => !isMatched && handleTranslationSelect(pair.translation)}
                            draggable={!isMatched}
                            onDragStart={(e) => handleDragStart(e, `translation-${index}`, 'translation', pair.translation)}
                            onDragOver={(e) => handleDragOver(e, pair.translation)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, pair.translation, 'translation')}
                            whileHover={!isMatched ? { scale: 1.02 } : undefined}
                            whileTap={!isMatched ? { scale: 0.98 } : undefined}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            layout
                          >
                            <div className="item-content">
                              <span className="item-text">{pair.translation}</span>
                              
                              <div className="item-actions">
                                {isMatched && (
                                  <CheckCircle size={20} className="match-icon" />
                                )}
                                
                                {isIncorrect && (
                                  <XCircle size={20} className="incorrect-icon" />
                                )}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Phase de session terminée avec statistiques détaillées */}
        {sessionPhase === 'complete' && (() => {
          const latestByExercise = new Map<string, typeof tempSessionData[0]>();
          tempSessionData.forEach(item => {
            const key = `${item.word}||${item.exerciseType || 'default'}`;
            const ex = latestByExercise.get(key);
            if (!ex || item.timestamp > ex.timestamp) latestByExercise.set(key, item);
          });
          const uniqueResults = Array.from(latestByExercise.values());
          const correct = uniqueResults.filter(r => r.isCorrect).length;
          const score = uniqueResults.length > 0 ? Math.round((correct / uniqueResults.length) * 100) : 0;
          const uniqueWords = Array.from(new Set(uniqueResults.map(r => r.word)));
          const mastered = uniqueWords.filter(w => {
            const wd = uniqueResults.filter(d => d.word === w);
            return wd.length > 0 && wd.filter(d => d.isCorrect).length / wd.length >= 0.75;
          }).length;

          const congrats = summaryCongrats;
          const proverb  = summaryProverb;

          return (
            <SummaryScreen
              theme={theme}
              label={t('revision.summaryLabel')}
              congrats={congrats}
              stats={[
                { value: `${score}%`, label: t('exercise.summary.score') },
                { value: `${correct}/${uniqueResults.length}`, label: t('revision.answers') },
                { value: `${mastered}/${uniqueWords.length}`, label: t('learnMode.summary.mastered') },
              ]}
              proverb={proverb}
              primaryBtn={{
                label: t('learnMode.summary.continueLearning'),
                onClick: async () => { await refreshRevisionProgress(); handleResetSession(); },
              }}
              onShare={onShare}
            >
              {uniqueWords.length > 0 && (
                <div className="summ-word-section">
                  <p className="summ-word-section__title">{t('revision.wordsRevised', { count: uniqueWords.length })}</p>
                  {uniqueWords.map((word, i) => {
                    const wd = uniqueResults.filter(d => d.word === word);
                    const c = wd.filter(d => d.isCorrect).length;
                    const ok = wd.length > 0 && c / wd.length >= 0.75;
                    return (
                      <div key={i} className="summ-word-row">
                        <span className="summ-word-row__word">{word}</span>
                        <span className="summ-word-row__translation">{c}/{wd.length}</span>
                        <span className={`summ-word-row__badge ${ok ? 'summ-word-row__badge--ok' : 'summ-word-row__badge--miss'}`}>
                          {ok ? '✓' : '↻'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </SummaryScreen>
          );
        })()}

        {/* Modal de sélection de catégorie — rendu via portal pour échapper au contain:paint */}
        {createPortal(
          <AnimatePresence>
            {showCategoryModal && selectedCategory && (
              <motion.div
                className="rev-cat-modal-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeCategoryModal}
            >
              <motion.div
                className="rev-cat-modal"
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.97 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                onClick={e => e.stopPropagation()}
                data-theme={theme}
              >
                <div className="rev-cat-modal__header">
                  <span className="rev-cat-modal__emoji">
                    <BookOpen size={20} strokeWidth={1.75} />
                  </span>
                  <h2 className="rev-cat-modal__title">{selectedCategory ? displayCategoryLabel(selectedCategory, t) : selectedCategory}</h2>
                  <span className="rev-cat-modal__count">
                    {t('revision.dueCount', { count: currentWords.length })}
                  </span>
                </div>

                <button className="rev-cat-modal__toggle" onClick={toggleWordsList}>
                  {showWordsList
                    ? <><ChevronUp size={14} /><span>{t('revision.hideList')}</span></>
                    : <><ChevronDown size={14} /><span>{t('revision.showWords')}</span></>
                  }
                </button>

                <AnimatePresence>
                  {showWordsList && (
                    <motion.div
                      className="rev-cat-modal__words"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22 }}
                    >
                      {currentWords.map(([word, data], i) => (
                        <div key={i} className="rev-cat-modal__row">
                          <span className="rev-cat-modal__word">{cleanParentheses(word)}</span>
                          <span className="rev-cat-modal__trans">{getWordTranslation(data, i18n.language)}</span>
                          {data.audio && (
                            <button
                              className="rev-cat-modal__audio"
                              onClick={e => { e.stopPropagation(); playWord(data.audio!); }}
                            >
                              <Volume2 size={14} />
                            </button>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="rev-cat-modal__actions">
                  <button className="rev-cat-modal__cancel" onClick={closeCategoryModal}>
                    {t('common.cancel')}
                  </button>
                  <motion.button
                    className="rev-cat-modal__start"
                    onClick={() => { setShowCategoryModal(false); startRevision(); }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <RotateCw size={16} />
                    <span>{t('revision.start')}</span>
                  </motion.button>
                </div>
              </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

        {/* Modal de statistiques avec z-index géré */}
        <RevisionStatsModal
          isOpen={showStatsModal}
          onClose={() => setShowStatsModal(false)}
          revisionHistory={loadSessionHistory().map(word => ({
            ...word,
            isCorrect: word.isCorrect ?? false,
            nextReview: word.nextReview ?? Date.now() + (24 * 60 * 60 * 1000)
          }))}
          wordDataMap={{ ...fullWordDataMap, ...wordDataMap }}
          wordProgress={userProgress.wordProgress}
          languageCode={languageCode}
          wordsToReview={revisionProgress.wordsToReview}
        />

        <ConfirmationModal
          isOpen={showExitConfirmation}
          onConfirm={handleExitConfirm}
          onCancel={() => setShowExitConfirmation(false)}
          message={t('revision.exitConfirm.message') as string}
          title={t('revision.exitConfirm.title') as string}
          confirmText={t('learnMode.exitConfirm.confirm') as string}
          cancelText={t('revision.exitConfirm.cancel') as string}
          confirmButtonStyle="danger"
          position="top"
        />
      </div>

    </div>
  );
};

export default RevisionMode;
